/**
 * @file apiContacts.js
 * @description Módulo de API para contactos e invitaciones.
 * Solo reorganización — sin cambio de lógica ni endpoints.
 */

import { API_ENDPOINTS, API_BASE, getBasePath, joinApiOrigin } from '../utils/constants';
import { devLog } from '../utils/devConsoleLog';
import { getToken } from '../services/apiClient';

const basePath = (API_BASE && API_BASE.startsWith('http')) ? '' : (API_BASE || getBasePath());

export const apiContactsMethods = {
  async getContacts(usuarioId, lang) {
    const langParam = lang ? `&lang=${encodeURIComponent(lang)}` : '';
    const endpoint = `${API_ENDPOINTS.CONTACTS_MANAGE}?usuario_id=${usuarioId}${langParam}`;
    const url = joinApiOrigin(endpoint);
    const token = getToken();
    if (!token) {
      devLog('[CONTACTS_CALL_BLOCKED_NO_TOKEN]', { endpoint, usuarioId });
      return { success: false, blocked: true, code: 'TOKEN_MISSING' };
    }
    devLog('🌐 [API] GET CONTACTS - Endpoint:', endpoint);
    try {
      const result = await this.request(endpoint, { _skipGlobalErrorModal: true });
      devLog('✅ [API] GET CONTACTS - Respuesta recibida:', {
        success: result?.success,
        dataLength: Array.isArray(result?.data) ? result?.data.length : 0,
        hasUsuarios: !!result?.usuarios
      });
      return result;
    } catch (error) {
      console.error('❌ Error en getContacts:', error);
      console.error('❌ Error stack:', error.stack);
      throw error;
    }
  },

  async getBlockedUsers(usuarioId) {
    return this.request(`${API_ENDPOINTS.BLOCKS}?usuario_id=${usuarioId}`, {
      _skipGlobalErrorModal: true,
    });
  },

  async toggleBlock(usuarioId, targetId, block = true) {
    return this.request(API_ENDPOINTS.BLOCKS, {
      method: 'POST',
      body: JSON.stringify({
        usuario_id: usuarioId,
        objetivo_id: targetId,
        action: block ? 'block' : 'unblock'
      })
    });
  },

  async getFavorites(usuarioId) {
    return this.request(`${API_ENDPOINTS.FAVORITES}?usuario_id=${usuarioId}`, {
      _skipDedupe: true,
    });
  },

  async updateContact(usuarioId, contactoId, alias = null, nota = null) {
    devLog('🔄 [API] updateContact llamado:', {
      usuarioId,
      contactoId,
      alias,
      nota,
      endpoint: API_ENDPOINTS.CONTACTS_MANAGE
    });
    const payload = {
      usuario_id: usuarioId,
      contacto_id: contactoId,
      alias: alias,
      nota: nota
    };
    devLog('🔥 [API] Payload JSON que se enviará:', JSON.stringify(payload, null, 2));
    devLog('🔥 [API] Endpoint completo:', API_ENDPOINTS.CONTACTS_MANAGE);
    try {
      const result = await this.request(API_ENDPOINTS.CONTACTS_MANAGE, {
        method: 'PUT',
        body: JSON.stringify(payload)
      });
      devLog('🔥 [API] Respuesta del servidor:', result);
      return result;
    } catch (error) {
      console.error('🔥 [API] ERROR en updateContact:', error);
      console.error('🔥 [API] Error details:', {
        message: error.message,
        status: error.status,
        isHtml: error.isHtml,
        data: error.data
      });
      throw error;
    }
  },

  async toggleFavorite(usuarioId, targetId, favorito = true, orden = 0) {
    const basePath = (import.meta.env?.VITE_API_BASE || '/api').startsWith('http') ? '' : (import.meta.env?.VITE_API_BASE || '/api');
    devLog('⭐ [FAVORITOS] toggleFavorite llamado', {
      usuarioId,
      targetId,
      favorito,
      orden,
      endpoint: API_ENDPOINTS.FAVORITES,
      basePath,
      urlCompleta: joinApiOrigin(API_ENDPOINTS.FAVORITES)
    });
    return this.request(API_ENDPOINTS.FAVORITES, {
      method: 'POST',
      body: JSON.stringify({
        usuario_id: usuarioId,
        target_id: targetId,
        favorito: favorito,
        orden: orden
      })
    });
  },

  async sendInvitation(invitationData) {
    return this.request(API_ENDPOINTS.SEND_INVITATION, {
      method: 'POST',
      body: JSON.stringify(invitationData),
      // InviteContactModal ya muestra modal funcional; evitar doble modal global (errorBus).
      _skipGlobalErrorModal: true,
    });
  },

  async validateInvitation(token) {
    return this.request(`${API_ENDPOINTS.VALIDATE_INVITATION}?token=${encodeURIComponent(token)}`);
  },

  async acceptInvitation(token) {
    return this.request(API_ENDPOINTS.ACCEPT_INVITATION, {
      method: 'POST',
      body: JSON.stringify({ token })
    });
  },

  async listInvitations() {
    return this.request(API_ENDPOINTS.LIST_INVITATIONS);
  },

  async getSentInvitations() {
    const res = await this.request(API_ENDPOINTS.LIST_INVITATIONS);
    const items = res?.data ?? res?.invitations ?? [];
    return { ok: !!res?.success, items: Array.isArray(items) ? items : [] };
  },

  /** POST /api/cancelar_invitacion.php — cuerpo { invitation_id: number } */
  async cancelInvitation(invitationId) {
    const id = Number(invitationId);
    return this.request(API_ENDPOINTS.CANCEL_INVITATION, {
      method: 'POST',
      body: JSON.stringify({ invitation_id: id }),
    });
  },

  async resendInvitation(invitationId) {
    return this.request(API_ENDPOINTS.RESEND_INVITATION, {
      method: 'POST',
      body: JSON.stringify({ invitation_id: invitationId })
    });
  }
};
